import React from 'react';
import { InputMeta, InputWrapper, ModifiedInput } from '.';
import cn from 'classnames';
import './inputTiles.scss';
import { ANIMATION_CHOICE_DELAY } from '../../../utils/global';

export interface InputTileOption<V = unknown> {
  title: string;
  value: V;
  desc?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  after?: React.ReactNode;
}

interface InputTileProps extends Omit<ModifiedInput, 'id'> {
  /**
   * On change sets a value obviously.
   * Make sure to pass a onClick as well, so you still have a action when the users clicks on the already selected tile
   */
  onChange: (value: unknown) => void;
  wrapperClass?: string;
  meta?: InputMeta;
  options: InputTileOption[];
  maxDelayIndex?: number;
  value?: unknown;
  compact?: boolean;
  id: string;
}

export const InputTiles: React.FC<InputTileProps> = ({
  wrapperClass,
  meta,
  options,
  onChange,
  onClick,
  id,
  name,
  value,
  maxDelayIndex = -1,
  compact,
}) => {
  const nameOrId = name || id;
  return (
    <InputWrapper {...meta} className={cn(wrapperClass)}>
      <div className={cn('w-full', { 'input-el--tile-compact': compact })}>
        {options.map((option, i) => {
          const selected =
            typeof value === 'object' ? JSON.stringify(value) === JSON.stringify(option.value) : value === option.value;
          const delayClass = maxDelayIndex === -1 ? i : i % maxDelayIndex;
          const identifiers = {
            'data-qa-id': `${nameOrId}_${option.value}_${i}`,
            'data-qa-input-name': nameOrId,
            'data-qa-input-value': option.value,
            'data-qa-nth-of-kind': i,
          };
          return (
            <React.Fragment key={`${id}_${i}`}>
              <input
                id={`${id}_${i}`}
                type={'radio'}
                name={nameOrId}
                onChange={() => onChange(option.value)}
                checked={selected}
                value={String(option.value)}
                className="sr-only"
              />
              <label
                htmlFor={`${id}_${i}`}
                /**
                 * NOTE About clicky:
                 * A click event is usefull for when you end up on a step were an existing value is prefilled,
                 * if you click on the selected value a change event won't be triggered.
                 *
                 * Why a tiny delay?
                 * - Let the animation finish so the user has a "selected" feedback
                 * - The click event does not propagate the select event
                 * - Delay is only needed if the value isn't selected
                 */
                onClick={onClick ? () => setTimeout(onClick, selected ? undefined : ANIMATION_CHOICE_DELAY) : undefined}
                {...identifiers}
                className={cn('cursor-pointer trigger', 'input-el--tile--option', `delay-index-${delayClass}`, {
                  'is-selected': selected,
                })}
              >
                {option.prefix && <div className={'input-el--tile--prefix mt-[0.2rem]'}>{option.prefix}</div>}
                <div className={cn('input-el--tile--copy', { '!pr-0': !option.suffix })}>
                  {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
                  <h6 className={'leading-[1.33rem]'} dangerouslySetInnerHTML={{ __html: option.title }} />
                  {option.desc && (
                    <div className={'input-el--tile--desc text-sm mt-1'} style={{ lineHeight: '19px' }}>
                      {option.desc}
                    </div>
                  )}
                </div>
                {option.suffix && typeof option.suffix !== 'string' && (
                  <div className={'input-el--tile--suffix'}>{option.suffix}</div>
                )}
                {option.suffix && typeof option.suffix === 'string' && (
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  <div className={'input-el--tile--suffix'} dangerouslySetInnerHTML={{ __html: option.suffix }} />
                )}
              </label>
              {option.after && option.after}
            </React.Fragment>
          );
        })}
      </div>
    </InputWrapper>
  );
};
